import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, of, Subject, switchMap } from 'rxjs';
import { StatusBar, StatusBarInfo, Style } from '@capacitor/status-bar';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { Capacitor } from '@capacitor/core';

@Injectable({
    providedIn: 'root',
})
export class StatusBarService {
    public bgLightColor = '#FFFFFF';
    public bgLightNavColor = '#FFFFFF';
    public bgLoginColor = '#FFFFFF';
    public bgDarkColor = '#FFFFFF';
    public bgDangerColor = '#ef6d6d';
    public statusTapEvent$ = new Subject<Event>();
    public previsionStatusInfo$ = new BehaviorSubject<StatusBarInfo | null>(null);

    constructor() {
        window.addEventListener('statusTap', ev => {
            this.statusTapEvent$.next(ev);
        });
    }

    public getInfo(): Observable<StatusBarInfo> {
        if (Capacitor.getPlatform() !== 'web') {
            return from(StatusBar.getInfo());
        }

        return of({
            visible: false,
            style: Style.Default,
        });
    }

    public setStatusBarStyleDark(): Observable<[void, void]> {
        if (Capacitor.getPlatform() !== 'web') {
            return this.getInfo().pipe(
                switchMap(info => {
                    this.previsionStatusInfo$.next(info);

                    return combineLatest([
                        from(StatusBar.setStyle({ style: Style.Dark })),
                        Capacitor.getPlatform() === 'android'
                            ? from(NavigationBar.setNavigationBarColor({ color: this.bgLightColor, darkButtons: true }))
                            : of(),
                    ]);
                }),
            );
        }

        return of();
    }

    public setNavBarStyleButtons(darkButtons: boolean): Observable<void> {
        if (Capacitor.getPlatform() !== 'web') {
            return this.getInfo().pipe(
                switchMap(info => {
                    return Capacitor.getPlatform() === 'android'
                        ? from(NavigationBar.setNavigationBarColor({ color: darkButtons ? this.bgLightColor : 'transparent', darkButtons }))
                        : of();
                }),
            );
        }

        return of();
    }

    public setStatusBarStyleLight(): Observable<[void, void]> {
        if (Capacitor.getPlatform() !== 'web') {
            return this.getInfo().pipe(
                switchMap(info => {
                    this.previsionStatusInfo$.next(info);

                    return combineLatest([
                        from(StatusBar.setStyle({ style: Style.Dark })),
                        Capacitor.getPlatform() === 'android'
                            ? from(
                                NavigationBar.setNavigationBarColor({
                                    color: this.bgLightColor,
                                    darkButtons: true,
                                }),
                            )
                            : of(),
                    ]);
                }),
            );
        }

        return of();
    }

    public setStatusBarStyleDanger(): Observable<void[]> {
        return combineLatest([
            from(StatusBar.setStyle({ style: Style.Dark })),
            from(StatusBar.setBackgroundColor({ color: this.bgDangerColor })),
        ]);
    }

    public setStatusBarStylePrevious(): Observable<[void, void] | void> {
        if (!this.previsionStatusInfo$.value?.color) {
            return of();
        }

        switch (this.previsionStatusInfo$.value?.color) {
            case this.bgDarkColor:
                return this.setStatusBarStyleDark();

            case this.bgLightColor:
                return this.setStatusBarStyleLight();

            case this.bgLoginColor:
                return this.setStatusLogin();
        }

        return of();
    }

    public setStatusLogin(): Observable<[void, void]> {
        const isAndroid = Capacitor.getPlatform() === 'android';
        if (Capacitor.getPlatform() !== 'web') {
            return this.getInfo().pipe(
                switchMap(info => {
                    this.previsionStatusInfo$.next(info);

                    return combineLatest([
                        from(StatusBar.setStyle({ style: Style.Light })),
                        isAndroid
                            ? from(
                                NavigationBar.setNavigationBarColor({ color: this.bgLightColor, darkButtons: true }),
                            )
                            : of(),
                    ]);
                }),
            );
        }

        return of();
    }

    public setStatusBarStyleDefault(matches: boolean): Observable<[void, void]> {
        if (matches) {
            return this.setStatusBarStyleDark();
        }

        return this.setStatusBarStyleLight();
    }
}
